import { KeyValue } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { VendorRequestDomain } from '../model/af.model';
import { VendorService } from '../service/vendor.service';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { CustomValidatorsService } from '../service/custom-validators.service';

@Component({
  selector: 'app-vendor-request-form',
  templateUrl: './vendor-request-form.component.html',
  styleUrls: ['./vendor-request-form.component.scss']
})
export class VendorRequestFormComponent implements OnInit {

  vendorRequestForm: UntypedFormGroup;
  vendorRequestData: VendorRequestDomain;

  regexName: RegExp = /^[A-Za-z\u00C0-\u017F\-',. ]+$/;
  regexSuffix: RegExp = /^[A-Za-z\u00C0-\u017F\-', .]+$/;
  regexEmail: RegExp = /^[a-zA-Z0-9@!#$%&'*+/=?^_`{|}~.-]*$/;
  regexNumber: RegExp = /^\d*$/;
  regexRefNumber: RegExp = /^[\dA-Za-z\/-]*$/;
  regexStr: RegExp = /^[a-zA-Z0-9\s.#\-/*@&"'():;]+$/;

  @ViewChild("vendorRequestElement") vendorRequestElement: ElementRef;

  constructor(private fb: UntypedFormBuilder, private vendorService: VendorService, private _snackBar: MatSnackBar,
    private customValidator: CustomValidatorsService) { }

  ngOnInit(): void {
    this.createGroup();
    this.subscribeEvents();

    this.vendorService.getFocusEmitter().subscribe({
      next: (data: any) => {
        if (data === 'vendorRequestForm') {
          this.vendorFormFocus();
        }
      },
      error: (error: Error) => {
        console.log(error);
      }
    });
  }

  vendorFormFocus() {
    setTimeout(() => {
      this.vendorRequestElement.nativeElement.focus();
    });
  }

  get firstName() { return this.vendorRequestForm.get('firstName') }
  get middleName() { return this.vendorRequestForm.get('middleName') }
  get email() { return this.vendorRequestForm.get('email') }
  get lastName() { return this.vendorRequestForm.get('lastName') }
  get suffix() { return this.vendorRequestForm.get('suffix') }
  get country() { return this.vendorRequestForm.get('country') }
  get jobCountry() { return this.vendorRequestForm.get('jobCountry') }
  get vendorAccount() { return this.vendorRequestForm.get('vendorAccount') }
  get vendorName() { return this.vendorRequestForm.get('vendorName') }
  get refNum() { return this.vendorRequestForm.get('refNum') }
  get costCenter() { return this.vendorRequestForm.get('costCenter') }
  get mailCode() { return this.vendorRequestForm.get('mailCode') }
  get componentsCheck() { return this.vendorRequestForm.get('componentsCheck') }
  get lockboxCheck() { return this.vendorRequestForm.get('lockboxCheck') }

  createGroup() {
    this.vendorRequestForm = this.fb.group({
      fingerPrintCheck: [false],
      actionBaseCheck: [false],
      ewsCheck: [false],
      applicantFormCheck: [false],
      credit: [false],
      ssnValidation: [false],
      ssnTrace: [false],
      nationalCriminalDatabase: [false],
      countyCriminal: [false],
      federalCriminal: [false],
      componentsCheck: [false],
      lockboxCheck: [false],
      firstName: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(this.regexName)]],
      middleName: ['', [Validators.maxLength(50), Validators.pattern(this.regexName)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(100), Validators.pattern(this.regexEmail)]],
      lastName: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(this.regexName)]],
      suffix: ['', [Validators.maxLength(10), Validators.pattern(this.regexSuffix)]],
      country: ['', [Validators.required, Validators.pattern(this.regexNumber)]],
      jobCountry: ['', [Validators.required, Validators.pattern(this.regexNumber)]],
      vendorAccount: ['', [Validators.required, Validators.pattern(this.regexNumber)]],
      vendorName: ['', [Validators.maxLength(100), Validators.pattern(this.regexStr)]],
      refNum: ['', [Validators.required, Validators.maxLength(30), Validators.pattern(this.regexRefNumber)]],
      costCenter: ['', [Validators.required, this.customValidator.globalValidation, this.customValidator.validComapyCode]],
      mailCode: [''],
      profileId: [''],
      vendorFirstName: [''],
      vendorLastName: [''],
      vendorEmailAddress: ['']
    });
  }

  subscribeEvents() {
    this.vendorService.getVendorRequest().subscribe({
      next: (data: VendorRequestDomain) => {
        this.vendorRequestData = data;
        this.vendorRequestForm.patchValue(data);
      }
    });
  }

  valueAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return a.value.localeCompare(b.value);
  }

  ascOrderVendorAccountFirst = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    try {
      if (a.value == this.vendorRequestData.vendorAccountName) {
        return -1;
      }
      if (b.value == this.vendorRequestData.vendorAccountName) {
        return 1;
      }
      return a.value.localeCompare(b.value);
    } catch (e) {
      console.log(e);
    }
  }

  validate() {
    this.vendorRequestForm.markAllAsTouched();
    return !this.vendorRequestForm.invalid;
  }

  onSubmit() {
    let config = new MatSnackBarConfig();
    let horizontalPosition: MatSnackBarHorizontalPosition = 'center';
    let verticalPosition: MatSnackBarVerticalPosition = 'top';
    config.verticalPosition = verticalPosition;
    config.horizontalPosition = horizontalPosition;
    config.duration = 5000;

    if (this.validate()) {
      let resMsg: string;
      this.vendorService.submitVendorRequest(this.vendorRequestForm.value).subscribe({
        next: (data) => {
          if (null != data.responseId) {
            resMsg = "The background check has been initiated successfully " +
              "Your case has been assigned number: " + data.responseId;
          } else {
            config.panelClass = 'submit-error';
            resMsg = "The background check was not initiated successfully " +
              "Following exceptions were received: " + data.errorMsg;
          }
          this._snackBar.open(resMsg, "Close", config);
          this.onReset();
        },
        error: (error: any) => {
          config.panelClass = 'submit-error';
          this._snackBar.open("Contractor Background Check was not Initiated", "Close", config);
        }
      });
    } else {
      config.panelClass = 'submit-error';
      this._snackBar.open("The required fields below have not been entered correctly", "Close", config);
    }
  }

  onReset() {
    this.vendorRequestForm.reset();
    this.vendorRequestForm.patchValue(this.vendorRequestData);
    this.country.setValue("186");
    this.jobCountry.setValue("186");
    this.vendorAccount.setValue(this.vendorRequestData.vendorAccountSerialNum);
  }
}
